/**
 * worksheets/$slug.tsx — Worksheet detail page (Task 22 + 2026-07-20 gating spec).
 *
 * Lives at `/worksheets/$slug`. Thin route wrapper that resolves the
 * slug from the catalog cache, hydrates `isEnabled` from catalog state,
 * checks session, and delegates to <WorksheetDetailSurface> for rendering.
 *
 * Gate logic:
 *   - WorksheetDetailSurface branches on isLoggedIn + isEnabled
 *   - Guest + ready → preview only (no PDF, no download)
 *   - Login + disabled → "Worksheet ini sedang tidak aktif"
 *   - Login + enabled → full PDF + download (existing)
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { WorksheetDetailSurface } from '../../components/public/WorksheetDetailSurface.js';
import {
  publicCatalogQuery,
  catalogStateQuery,
  sessionQuery,
  mergeCatalogWithState,
  findWorksheetBySlug,
  myProgressQuery,
} from '../../lib/api/queries.js';
import { submitProgressMutation } from '../../lib/api/mutations.js';
import {
  buildWorksheetProgressEvent,
  recordsWorksheetProgress,
} from '../../lib/worksheet-progress.js';

export const Route = createFileRoute('/worksheets/$slug')({
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(publicCatalogQuery);
    return null;
  },
  component: WorksheetDetailPage,
});

function WorksheetDetailPage(): ReactNode {
  const { slug } = Route.useParams();
  const catalog = useQuery(publicCatalogQuery);
  const state = useQuery(catalogStateQuery);
  const session = useQuery(sessionQuery);

  const stateMap = state.data?.ok ? (state.data.data?.worksheets ?? {}) : {};
  const worksheets = mergeCatalogWithState(
    catalog.data?.worksheets ?? [],
    stateMap,
    'worksheetId',
  );
  const worksheet = findWorksheetBySlug(worksheets, slug);

  const queryClient = useQueryClient();
  const role = session.data?.ok ? (session.data.data?.user?.role ?? null) : null;
  const isLoggedIn = !!(session.data?.ok && session.data.data?.user);
  const isStudent = isLoggedIn && recordsWorksheetProgress(role);
  const progress = useQuery({ ...myProgressQuery, enabled: isStudent });
  // Separate instances so the silent "opened" ping never disables the done button.
  const openProgress = useMutation(submitProgressMutation());
  const submitProgress = useMutation({
    ...submitProgressMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['progress', 'me'] }),
  });
  const worksheetId = worksheet?.worksheetId;
  const canOpen = !!worksheet && worksheet.status === 'ready' && worksheet.isEnabled !== false;
  const openedRef = useRef<string | null>(null);

  // Opening the worksheet page = "worksheet_started" (once per page view).
  useEffect(() => {
    if (!isStudent || !canOpen || !worksheetId) return;
    if (openedRef.current === worksheetId) return;
    openedRef.current = worksheetId;
    openProgress.mutate([buildWorksheetProgressEvent(worksheetId, 'started')]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, canOpen, worksheetId]);

  if (catalog.isLoading) {
    return (
      <div className="page-shell">
        <p className="form-message">Memuat katalog…</p>
      </div>
    );
  }

  if (!worksheet) {
    return (
      <div className="page-shell">
        <PageHeader
          eyebrow="Worksheet"
          title="Worksheet tidak ditemukan"
          description={`Kode '${slug}' tidak cocok dengan worksheet apa pun di katalog.`}
        />
        <section className="section">
          <StatePanel
            variant="error"
            title="Slug tidak dikenal"
            description="Pilih worksheet dari katalog publik atau kembali ke dasbor kamu."
            action={
              <Link className="button button-primary" to="/worksheets">
                Buka katalog
              </Link>
            }
          />
        </section>
      </div>
    );
  }

  const isTutor = role === 'tutor' || role === 'admin';
  const isMarkedDone = !!(
    progress.data?.ok &&
    progress.data.data?.worksheets?.some((w) => w.worksheetId === worksheet.worksheetId && w.completed)
  );

  return (
    <WorksheetDetailSurface
      worksheet={worksheet}
      isLoggedIn={isLoggedIn}
      isTutor={isTutor}
      onMarkDone={
        isStudent
          ? () => submitProgress.mutate([buildWorksheetProgressEvent(worksheet.worksheetId, 'completed')])
          : undefined
      }
      isMarkedDone={isMarkedDone}
      markDonePending={submitProgress.isPending}
    />
  );
}